import { createStyles, Group, Loader, Stack } from "@mantine/core";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import React, { useState } from "react";
import { Button, Toggle, tokens } from "@blurb/codex-react";
import { RpaButtonLink } from "../../../components/RpiLink";
import { useNavigate, useParams } from "react-router-dom";
import { RpaJsonInput } from "../../../components/inputFields/RpaJsonInput";
import { IconV3, RpiAlert, RpiLabelText, useShowSuccessToast } from "@rpi/openapi-core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useDebouncedValue } from "@mantine/hooks";

const isValidPrice = (value: string) => {
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return false;
  return parseFloat(value) > 0;
};

const useToggleStyles = createStyles(() => ({
  toggle: {
    "& .base-Switch-input:checked + .base-Switch-track": {
      backgroundColor: tokens.color.semantic.bg.primary,
    },
  },
}));

export const OpenApiProductSkuEditor = () => {
  const { id } = useParams();
  if (!id) {
    throw new Error("Missing SKU ID");
  }
  const numId = Number.parseInt(id);
  if (Number.isNaN(numId)) {
    throw new Error("Invalid SKU ID");
  }

  const { data: openApiProductData, isLoading } = GraphQL.useOpenApiProductQuery({ id: numId });
  const initialOpenApiProduct = openApiProductData?.openapiProduct;

  if (isLoading) {
    return <Loader />;
  } else if (!initialOpenApiProduct) {
    throw new Error("SKU not found");
  }

  return (
    <OpenApiProductSkuNavigator
      initialSkuData={{
        manufacturingSku: initialOpenApiProduct.openapiSku,
        uosSku: initialOpenApiProduct.uosSku,
        attributes: JSON.stringify(initialOpenApiProduct.skuAttributes) ?? "",
        specification: JSON.stringify(initialOpenApiProduct.productSpecification?.specification) ?? "",
        basePrice: initialOpenApiProduct.productPricing?.[0].basePrice.toString() ?? "",
        perPagePrice: initialOpenApiProduct.productPricing?.[0].perPageCost.toString() ?? "",
        enabled: initialOpenApiProduct.enabled,
      }}
      successMessage="SKU was updated successfully!"
    />
  );
};

interface OpenApiProductSkuNavigatorProps {
  initialSkuData?: GraphQL.UpsertOpenapiProductInput;
  successMessage?: string;
}

export const OpenApiProductSkuNavigator: React.FC<OpenApiProductSkuNavigatorProps> = ({
  initialSkuData,
  successMessage = "SKU was added successfully!",
}) => {
  const navigate = useNavigate();
  const { classes } = useToggleStyles();

  const form = useForm<GraphQL.UpsertOpenapiProductInput>({
    initialValues: {
      manufacturingSku: initialSkuData ? initialSkuData.manufacturingSku : "",
      uosSku: initialSkuData ? initialSkuData.uosSku : "",
      attributes: initialSkuData ? initialSkuData.attributes : "",
      specification: initialSkuData ? initialSkuData.specification : "",
      basePrice: initialSkuData ? initialSkuData.basePrice : "",
      perPagePrice: initialSkuData ? initialSkuData.perPagePrice : "",
      enabled: initialSkuData ? initialSkuData.enabled : true,
    },
    validate: {
      manufacturingSku: (value) => {
        if (!value) return "Please enter a manufacturing SKU";
        if (!existingSkuCheckLoading && existingSkuCheck?.openapiProductExists) {
          return "This manufacturing SKU already exists.";
        }
      },
      uosSku: (value) => (value ? null : "Please enter the UOS SKU"),
      attributes: (value) => (value ? null : "Please enter the attributes JSON"),
      specification: (value) => (value ? null : "Please enter the specification JSON"),
      basePrice: (value) => {
        if (!value) return "Please enter a base price";
        return isValidPrice(value) ? null : "Please enter a valid price";
      },
      perPagePrice: (value) => {
        if (!value) return "Please enter an additional cost";
        return isValidPrice(value) ? null : "Please enter a valid price";
      },
    },
  });

  const [debouncedSku] = useDebouncedValue(form.values.manufacturingSku, 500);

  const { data: existingSkuCheck, isLoading: existingSkuCheckLoading } = GraphQL.useOpenApiProductExistsQuery(
    {
      sku: debouncedSku,
    },
    {
      enabled: !initialSkuData && debouncedSku.length > 0,
    }
  );

  const showSuccessToast = useShowSuccessToast();
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  const upsertOpenApiProduct = GraphQL.useAdminUpsertOpenApiProductMutation({
    onSuccess: () => {
      showSuccessToast(successMessage);
      navigate("..");
    },
    onError: (error) => {
      setErrorText("Unable to save changes due to a network error. Please try again.");
      console.error("Failed to save sku", error);
    },
  });

  const isLoading = upsertOpenApiProduct.isLoading;

  const handleSubmit = (values: GraphQL.UpsertOpenapiProductInput) => {
    upsertOpenApiProduct.mutate({ input: values });
  };

  return (
    <Stack>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <RpaTextInput
            label="Manufacturing SKU"
            placeholder="Enter"
            required={true}
            disabled={isLoading || !!initialSkuData}
            {...form.getInputProps("manufacturingSku")}
          />
          <RpaTextInput
            label="UOS SKU"
            placeholder="Enter"
            required={true}
            disabled={isLoading}
            {...form.getInputProps("uosSku")}
          />
          <RpaJsonInput
            label="Attributes JSON"
            placeholder="Enter description"
            required={true}
            validationError="Please enter formatted JSON"
            formatOnBlur
            autosize
            minRows={4}
            disabled={isLoading}
            {...form.getInputProps("attributes")}
          />
          <RpaJsonInput
            label="Specification JSON"
            placeholder="Enter description"
            required={true}
            validationError="Please enter formatted JSON"
            formatOnBlur
            autosize
            minRows={4}
            disabled={isLoading}
            {...form.getInputProps("specification")}
          />
          <RpaTextInput
            label="Base Price"
            placeholder="0.00"
            required={true}
            icon={<IconV3 icon="attach_money" size={20} color="#464646" />}
            disabled={isLoading}
            {...form.getInputProps("basePrice")}
          />
          <RpaTextInput
            label="Additional Cost"
            placeholder="0.00"
            required={true}
            icon={<IconV3 icon="attach_money" size={20} color="#464646" />}
            disabled={isLoading}
            {...form.getInputProps("perPagePrice")}
          />
          <Group spacing="xs" align="center">
            <Toggle
              className={classes.toggle}
              disabled={isLoading || !!initialSkuData}
              pressed={form.values.enabled ?? true}
              onPressedChange={(pressed) => form.setFieldValue("enabled", pressed)}
            />
            <RpiLabelText>Enable SKU</RpiLabelText>
          </Group>
          {errorText && <RpiAlert>{errorText}</RpiAlert>}
          <Button variant="filled" intent="primary" type="submit" data-testid="saveNewSkuButton" disabled={isLoading}>
            Save Changes
          </Button>
          <Group position="center">
            <RpaButtonLink onClick={() => navigate("..")}>Cancel</RpaButtonLink>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};
